import socketIO from "socket.io-client";
import {setSuccess, setError, setActionAuthentication, setCurrentBooking} from "../../reducers/configSlice";
import {END_ACTION} from "../../constants/default";
import {STORAGE} from "../storage";
import {dispatchAction} from "../state/dispatchAction";
import { EventBus } from "../EventBus";
import * as SocketHandlers from "./listenerHandlers/handlers";
import {showToastMessage} from "../toastMessage";

let socket
let options = {
    reconnection: true,
    reconnectionDelay: 7500,
    reconnectionDelayMax : 5000,
    reconnectionAttempts: Infinity,
    secure: true,
    port: 3443
}

export const initSocketService = async () => {
    // SAFETY GUARD: Never initialize Socket.IO if soketiUrl exists
    const soketiUrl = await STORAGE.get("soketiUrl");
    if (soketiUrl) {
        console.warn('SOCKET - Blocked Socket.IO initialization: soketiUrl exists, client should use Soketi only');
        showToastMessage('Using upgraded connection service', 2000);
        return;
    }

    let socketUrl = await STORAGE.get("socketUrl")
    let token = await STORAGE.get("token")
    let storedUuid = await STORAGE.get('deviceUuid');

    if (!socketUrl || !token) {
        if (storedUuid) {
            showToastMessage('SOCKET - Unable to connect. No socket URL or Bearer Token.', 10000)
        }

        return;
    }

    console.log('SOCKET - Initializing Socket.IO service (no soketiUrl found)');
    await connectSocket(socketUrl, token);
    await setupSocketListeners();
}


const connectSocket = async (socketUrl, token) => {
    if (socketUrl.indexOf('yarooms') !== -1 && socketUrl.indexOf('.e.yarooms') === -1) {
        socketUrl = socketUrl.substring(0, socketUrl.indexOf('?token='))

        options.extraHeaders = {
            "bearer-token": token
        };
    }

    socket = socketIO(socketUrl, options);
};

const setupSocketListeners = () => {
    const listeners = [
        { event: 'connect', handler: SocketHandlers.handleConnect },
        { event: 'disconnect', handler: SocketHandlers.handleDisconnect(socket) },
        { event: 'connect_error', handler: SocketHandlers.handleConnectError },
        { event: 'error', handler: SocketHandlers.handleError },
        { event: 'delete', handler: SocketHandlers.handleDelete },
        { event: 'force_disconnect', handler: SocketHandlers.handleForceDisconnect },
        { event: 'config', handler: SocketHandlers.handleConfig(socket) },
        { event: 'refresh_token', handler: SocketHandlers.handleRefreshToken },
    ];

    listeners.forEach(({ event, handler }) => {
        socket.off(event);
        socket.on(event, handler);
    });
};

export const removeSocket = () => {
    if (!socket) return;
    socket.removeAllListeners();
    socket.disconnect();
    socket.close()

    socket = null;
}

export const emitFutureBookingList = (date = null) => {
    if (!socket || !socket.connected) return;

    if (date) {
        socket.emit('request_booking_list', date)
        return;
    }

    socket.emit('request_booking_list')
}

export const emitMaintenanceStatus = () => {
    if (!socket || !socket.connected) return;
    socket.emit('request_maintenance')
}

export const reconnectSocket = async () => {
    // SAFETY GUARD: Never reconnect Socket.IO if soketiUrl exists
    const soketiUrl = await STORAGE.get("soketiUrl");
    if (soketiUrl) {
        console.warn('SOCKET - Blocked Socket.IO reconnection: soketiUrl exists, client should use Soketi only');
        showToastMessage('Using upgraded connection service', 2000);
        return;
    }

    showToastMessage('SOCKET - Attempting reconnection...', 10000);

    if (socket && socket.connected) {
        showToastMessage('SOCKET - Already connected.');
        return;
    }

    if (socket) {
        socket.connect();
        return;
    }

    const socketUrl = await STORAGE.get("socketUrl");
    const token = await STORAGE.get("token");

    if (!socketUrl || !token) {
        const errorMessage = !socketUrl ? 'No socket URL.' : 'No Bearer Token.';
        showToastMessage(`SOCKET - Unable to reconnect. ${errorMessage}`);
        removeSocket()
        EventBus.emit('destroyInstance');
        return;
    }

    await initSocketService();
    showToastMessage('SOCKET - Reconnection attempt finished.', 5000);
};

export const addActionAuthenticationListener = async (action, callback = null) => {
    // Check if Soketi is active - if so, don't execute Socket.IO logic
    const soketiUrl = await STORAGE.get("soketiUrl");
    if (soketiUrl) {
        return;
    }

    // Only execute if Socket.IO is the active service
    if (!socket || !socket.connected) {
        console.warn('SOCKET - Cannot add action listener: not connected');
        return;
    }

    const eventName = 'action_authorization_' + action.code;
    const handler = async (msg) => {
        dispatchAction(setActionAuthentication(null));

        if (msg.status === 1) {
            emitFutureBookingList();

            if (callback) {
                callback();
            }

            if (action.action === END_ACTION) {
                dispatchAction(setCurrentBooking(null));
            }

            dispatchAction(setSuccess(msg.message + '. ' + action.successMessage));
        } else {
            dispatchAction(setError(msg.message));
        }

        socket.off(eventName, handler); // Unregister the event listener after handling the event
    };

    socket.on(eventName, handler);
};

export const isConnected = () => {
    return socket && socket.connected;
}